import axios from "axios";
import { config } from "../config";

const API_BASE = config.apiBaseUrl;

export type Category = {
  id: number;
  name: string;
  parentId: number | null;
  children?: Category[];
};

export type CategoryRequest = {
  name: string;
  parentId: number | null;
};

const getAuth = () => {
  const rawToken = localStorage.getItem("token");
  if (!rawToken) return {};
  // Preserve JWT-safe characters including dots ("header.payload.signature").
  const cleanedToken = rawToken.replace(/[^-A-Za-z0-9_.=]/g, '');
  // Only send a structurally valid JWT; otherwise omit the header entirely so
  // public endpoints (e.g. GET /categories) aren't rejected with 400 malformed.
  if (cleanedToken === '' || cleanedToken.split('.').length !== 3) {
    return {};
  }
  return { Authorization: `Bearer ${cleanedToken}` };
};

/**
 * Fetch a public endpoint, tolerating a stale/expired stored token:
 * if the server rejects the attached credentials, retry anonymously.
 */
const getPublic = async (url: string) => {
  try {
    return await axios.get(url, { headers: getAuth() });
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 400 || status === 401) {
      return await axios.get(url);
    }
    throw error;
  }
};

/**
 * Fetch the full category tree (public).
 */
export const fetchCategoryTree = async (): Promise<Category[]> => {
  const response = await getPublic(`${API_BASE}/categories`);
  return response.data;
};

/**
 * Fetch all categories as a flat list (public).
 * Useful for building multi-level selects in the upload form.
 */
export const fetchFlatCategories = async (): Promise<Category[]> => {
  const response = await getPublic(`${API_BASE}/categories/flat`);
  return response.data;
};

/**
 * Create a new category. Admin / category-admin only.
 */
export const createCategory = async (request: CategoryRequest): Promise<Category> => {
  const response = await axios.post(`${API_BASE}/categories`, request, { headers: getAuth() });
  return response.data;
};

/**
 * Update an existing category. Admin / category-admin only.
 */
export const updateCategory = async (id: number, request: CategoryRequest): Promise<Category> => {
  const response = await axios.put(`${API_BASE}/categories/${id}`, request, { headers: getAuth() });
  return response.data;
};

/**
 * Delete a category. Admin / category-admin only.
 */
export const deleteCategory = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/categories/${id}`, { headers: getAuth() });
};
